import { AxiosRequestConfig, AxiosResponse } from 'axios';
import Moment from 'moment';
import { axiosInstance, IServerSideGetRowsParams, PaginatedApiResponse } from '@fides/fides-react-components';
import { ColDef, SortModelItem } from '@ag-grid-community/core';
import { useMemo } from 'react';

export const mapSortModel = (columnMapping: ColDef[], sortModel: SortModelItem[]): string[] | undefined => {
  if (!sortModel.length) return undefined;

  return sortModel.map(({ sort, colId }) => {
    const colDef: ColDef = columnMapping.find((row) => row.colId === colId);
    const sortCol: string = colDef?.field ?? colId;
    return `${sortCol},${sort}`;
  });
};

type FilterModel = { [p: string]: any };
export const mapFilterModel = (filterModel: FilterModel, columnMapping?: ColDef[]) => {
  const constructFromPath = (path: string, value: unknown, cursor: Record<string, any>): void => {
    if (!path) return;

    const segments = path.split('.');
    let current = cursor;

    segments.forEach((segment, index) => {
      if (index === segments.length - 1) {
        current[segment] = value;
      } else {
        current[segment] = current[segment] || {};
        current = current[segment];
      }
    });
  };

  return Object.entries(filterModel).reduce((acc: any, cur) => {
    const [key, value]: [string, any] = cur;
    const queryKey = columnMapping?.[key] || key;

    if (value.filterType === 'text') {
      constructFromPath(queryKey, value.filter, acc);
      return acc;
    }

    if (value.filterType === 'number') {
      acc[`${queryKey}Range`] = {
        lowerBound: value.filter,
        upperBound: value.filterTo ? value.filterTo : value.filter,
      };
      return acc;
    }

    if (value.filterType === 'set') {
      const columnDef = columnMapping.find((col) => col.colId === key || col.field === key);
      const keySetFilter = columnDef?.filterParams?.key ? columnDef.filterParams.key : queryKey;
      acc[keySetFilter] = value.values.map((val: any) => (val === null ? '' : val));
      return acc;
    }

    if (value.filterType === 'date') {
      const columnDef = columnMapping.find((col) => col.colId === key || col.field === key);
      if (columnDef?.type === 'dateWithZoneId') {
        acc[`${queryKey}Range`] = value;
        value.dateFrom = value.dateFrom ? value.dateFrom.substring(0, 10) : null;
        value.dateTo = value.dateTo ? value.dateTo.substring(0, 10) : null;
        value.zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } else {
        const finalFrom = Moment(value.dateFrom).utc(true);
        const finalTo = value.dateTo ? Moment(value.dateTo).utc(true) : finalFrom;
        acc[`${queryKey}Range`] = { lowerBound: finalFrom, upperBound: finalTo };
      }

      return acc;
    }
    return acc;
  }, {});
};

export type RequestConfigInjector = (
  params: IServerSideGetRowsParams,
  requestConfig: AxiosRequestConfig
) => AxiosRequestConfig;

export type DataExtractor = (
  params: IServerSideGetRowsParams,
  responseData: PaginatedApiResponse<any>
) => [data: any, dataCount: number];

export const createDatasource = <T,>(
  config: AxiosRequestConfig,
  columnMapping: ColDef[],
  requestConfigInjector?: RequestConfigInjector,
  dataExtractor?: DataExtractor
) => {
  return {
    async getRows(params: IServerSideGetRowsParams) {
      const { startRow, endRow, filterModel, sortModel } = params.request;
      const { filter, onlyDeleted } = config.params;

      const columnFilters = filterModel ? mapFilterModel(filterModel, columnMapping) : {};
      let requestConfig: AxiosRequestConfig = structuredClone(config);

      requestConfig.params.filter = {
        ...config.params.filter,
        ...columnFilters,
      };

      if (filter?.globalSearch) {
        requestConfig.params.filter = { ...requestConfig.params.filter, globalSearch: filter.globalSearch };
      }

      if (filter?.defaultFilter) {
        const defaultFilter = { ...filter.defaultFilter };
        const duplicateKeys = Object.keys(columnFilters).filter((key) => filter?.defaultFilter[key]);
        duplicateKeys.forEach((key) => {
          defaultFilter[key] = columnFilters[key];
        });
        requestConfig.params.filter = { ...requestConfig.params.filter, ...defaultFilter };
      }

      requestConfig.params.sort = mapSortModel(columnMapping, sortModel);
      requestConfig.params.size = endRow - startRow;
      requestConfig.params.onlyDeleted = onlyDeleted;
      requestConfig.params.page = Math.ceil(endRow / requestConfig.params.size) - 1;

      // Apply request config injector if provided
      if (requestConfigInjector) {
        requestConfig = requestConfigInjector(params, requestConfig);
      }

      let response: AxiosResponse;
      try {
        response = await axiosInstance.request<PaginatedApiResponse<T>>(requestConfig);
      } catch {
        params.fail();
        return;
      }

      if (response?.data) {
        let rowData = response.data;
        let rowCount = response.data.totalElements || 0;

        // Apply data extractor if provided
        if (dataExtractor) {
          [rowData, rowCount] = dataExtractor(params, response.data);
        }

        params.success({
          rowData: rowData?.content || rowData,
          rowCount,
        });
      } else {
        // Inform grid request failed
        params.fail();
        // Display no elements
        params.success({ rowData: [], rowCount: 0 });
      }
    },
  };
};

export const useDatasource = <T,>(
  config: AxiosRequestConfig,
  columnMapping: ColDef[],
  requestConfigInjector?: RequestConfigInjector,
  dataExtractor?: DataExtractor
) => {
  const stableConfig: AxiosRequestConfig = useMemo(() => config, [JSON.stringify(config)]);
  const stableColumnMapping: ColDef[] = useMemo(() => columnMapping, [JSON.stringify(columnMapping)]);
  return useMemo(
    () => createDatasource<T>(stableConfig, stableColumnMapping, requestConfigInjector, dataExtractor),
    [stableConfig.params?.filter, requestConfigInjector, dataExtractor]
  );
};
